"use client";

import { useCallback } from "react";
import { trackAddToCart } from "@/lib/analytics";
import type { CartItem } from "@/lib/cart";
import { useCart } from "@/stores/cart-store";

/**
 * A cart line as the add-to-cart surfaces build it.
 *
 * Identical to `addItem`'s parameter except that `title` is REQUIRED: it is the
 * one field GA4's `add_to_cart` item cannot be assembled without, so making it
 * mandatory here means a cart write can never produce an incomplete event. All
 * five real call sites already pass it.
 */
export type AddToCartLine = Omit<CartItem, "quantity" | "title"> & {
  title: string;
  /** Defaults to 1, then clamped exactly as `lib/cart.addItem` clamps it. */
  quantity?: number;
};

/**
 * Write a line to the cart AND report it to GA4 — the only supported way to add
 * to the cart from a component.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * `addItem(...)` + `trackAddToCart(...)` was pasted at five sites, and
 * `components/products/detail/ProductDetailAddToCart.tsx` — the canonical `/p/<slug>`
 * buy box, i.e. the HIGHEST-intent surface and the Merchant Center landing page —
 * was the copy that omitted the GA4 call. Every PDP add went unrecorded, so
 * `add_to_cart` under-counted and cart→checkout conversion was unmeasurable
 * exactly where it mattered most. Nothing failed: the four other copies kept the
 * event alive, so `analytics-wiring.spec.ts`' "has at least one caller" floor
 * stayed green.
 *
 * The fix is structural rather than "add the missing line": the tracking call is
 * now unreachable-by-omission because the cart write and the event are the same
 * function call. `tests/unit/components/products/add-to-cart-analytics.spec.tsx`
 * (ADDCARTGA-3) additionally sweeps `app/`, `components/` and `hooks/` from disk
 * and FAILS if any file writes the cart without going through here.
 *
 * ── WHAT IS DELIBERATELY *NOT* IN HERE ──────────────────────────────────────
 * Only the two steps that are byte-identical at all five sites. The rest measured
 * genuinely different and each attempt to absorb it would have changed behaviour:
 *  - the pre-conditions differ (sold-out + un-chosen-variant on the PDP, a
 *    `hasVariants` bail on the cross-sell strip, an `enquireOnly` `toast.info` on
 *    the carousel),
 *  - the toast copy differs ("Added to cart" vs "<title> added to cart"),
 *  - the payload differs on purpose (the PDP omits `imageUrlRaw` — see the note
 *    in ProductDetailAddToCart — while the list surfaces carry it),
 *  - and two sites do extra work afterwards (a 400 ms re-click debounce, closing
 *    the variant drawer).
 * A hook that swallowed those would be a worse abstraction than the duplication.
 *
 * ── THE ONE INVARIANT IT ENFORCES ───────────────────────────────────────────
 * GA4 is handed the SAME price and quantity that were snapshotted into the cart,
 * derived from the line rather than recomputed. `trackAddToCart` divides
 * `pricePence` by 100 internally, so a second derivation is how `add_to_cart`
 * revenue ends up 100× off or disagreeing with `view_item_list`.
 */
export function useAddToCart(): (line: AddToCartLine) => void {
  const addItem = useCart((s) => s.addItem);

  return useCallback(
    (line: AddToCartLine) => {
      // Same clamp `lib/cart.addItem` applies, resolved ONCE here so the cart and
      // the analytics event cannot disagree about how many were added.
      const quantity = Math.max(1, Math.floor(line.quantity ?? 1));
      addItem({ ...line, quantity });
      trackAddToCart({
        productId: line.productId,
        title: line.title,
        category: line.category,
        pricePence: line.priceSnapshot,
        quantity,
      });
    },
    [addItem]
  );
}
